import { StoreError } from '@/types/storeError.interface';
import {
  IExternalLookup,
  IndexedExternalLookups,
  IndexedInternalLookups,
} from '@/typings';
import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '@/store';

interface LookupState {
  loading: boolean;
  error: StoreError | null;
  internalLookups: IndexedInternalLookups;
  externalLookups: IndexedExternalLookups;
}

const initialState: LookupState = {
  internalLookups: {},
  externalLookups: {},
  loading: false,
  error: null,
};

export const lookupSlice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    lookupLoading: state => {
      state.loading = true;
    },
    fetchInternalLookupListSuccess: (
      state,
      action: PayloadAction<IndexedInternalLookups>,
    ) => {
      state.loading = false;
      state.error = null;
      state.internalLookups = action.payload;
    },
    setExternalLookupOptions: (
      state,
      action: PayloadAction<Omit<IExternalLookup, 'values'>>,
    ) => {
      state.externalLookups[action.payload.fieldName] = {
        ...state.externalLookups[action.payload.fieldName],
        ...action.payload,
      };
    },
    fetchExternalLookupSuccess: (
      state,
      action: PayloadAction<Omit<IExternalLookup, 'options'>>,
    ) => {
      state.loading = false;
      state.error = null;
      state.externalLookups[action.payload.fieldName] = {
        ...state.externalLookups[action.payload.fieldName],
        ...action.payload,
      };
    },
    clearExternalLookupSuccess: (
      state,
      action: PayloadAction<Pick<IExternalLookup, 'fieldName'>>,
    ) => {
      state.loading = false;
      state.error = null;
      state.externalLookups[action.payload.fieldName] = {
        ...state.externalLookups[action.payload.fieldName],
        values: [],
      };
    },
    fetchLookupListFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  lookupLoading,
  fetchInternalLookupListSuccess,
  setExternalLookupOptions,
  fetchExternalLookupSuccess,
  clearExternalLookupSuccess,
  fetchLookupListFailure,
} = lookupSlice.actions;

export const selectLookup = (state: RootState) =>
  state[lookupSlice.name] as typeof initialState;
